'use client'

import { useRef, useState } from 'react'

import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'

import { useAuth, AUTH_TYPES } from './AuthContext'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

export default function LoginForm() {
    const { login, setSelectedType, externalAuthRequested, externalAuthLoading, oauthClientData } = useAuth()
    const emailInput = useRef(null)
    const passwordInput = useRef(null)

    const [revealPassword, setRevealPassword] = useState(false)
    const [loading, setLoading] = useState(false)

    const submitLogin = async () => {
        if (loading) return
        setLoading(true)
        try {
            await login(emailInput.current.value, passwordInput.current.value)
        } finally {
            setLoading(false)
        }
    }

    const handlePasswordEyeClick = () => {
        setRevealPassword(!revealPassword)
        setTimeout(() => {
            passwordInput.current.focus()
        }, 50)
    }

    return (
        <>
            <form
                className={classes('form')}
                onSubmit={(e) => e.preventDefault()}
                onKeyUp={({ key }) => key == 'Enter' && submitLogin()}
            >
                {oauthClientData && (
                    <div className={classes('oauth-client-info')}>
                        {oauthClientData.logoUrl && (
                            <img
                                src={oauthClientData.logoUrl}
                                alt={oauthClientData.name}
                                className={classes('client-logo')}
                            />
                        )}
                        <h1 className={classes('title')}>Sign in to authorise {oauthClientData.name}</h1>
                        <p className={classes('subtext')}>
                            {oauthClientData.description || 'Please enter your account details to continue.'}
                        </p>
                    </div>
                )}
                {!oauthClientData && (
                    <>
                        <h1 className={classes('title')}>Login to your account</h1>
                        <p className={classes('subtext')}>Welcome back! Please enter your details.</p>
                    </>
                )}
                <div className={classes('input-container')}>
                    <Input
                        type="email"
                        placeholder="Your email"
                        required={true}
                        ref={emailInput}
                        className={classes('icon-input')}
                        autoComplete="username"
                    />
                    <div className={classes('error-icon')}>
                        <Icon name="alert-circle" width={20} height={20} />
                    </div>
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type={revealPassword ? 'text' : 'password'}
                        placeholder="Password"
                        title={
                            'Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1 uppercase letter, 1 number and 1 special character (!@#$%^&*).'
                        }
                        ref={passwordInput}
                        required={true}
                        className={classes('icon-input')}
                        autoComplete="current-password"
                    />
                    <div
                        className={classes('password-reveal', {
                            'password-reveal--active': revealPassword,
                        })}
                        onMouseDown={handlePasswordEyeClick}
                    >
                        {revealPassword && <Icon name="eye" width={20} height={20} />}
                        {!revealPassword && <Icon name="eye-off" width={20} height={20} />}
                    </div>
                </div>
                <a href="#" onClick={() => setSelectedType(AUTH_TYPES.PASSWORD_RESET)}>
                    Forgotten your password?
                </a>
            </form>
            <Button
                className={classes('login-button')}
                onClick={(e) => {
                    e.preventDefault()
                    submitLogin()
                }}
                text={loading ? 'Logging in...' : 'Login'}
                disabled={loading}
            />
            <div className={classes('or-separator')}>
                <div className={classes('line')}></div>
                <span>or</span>
                <div className={classes('line')}></div>
            </div>
            <div className={classes('buttons')}>
                <button
                    className={classes('auth-button')}
                    disabled={externalAuthLoading || loading}
                    onClick={externalAuthRequested}
                >
                    <Icon name="google" />
                    <span>{externalAuthLoading ? 'Authenticating...' : 'Sign in with Google'}</span>
                </button>
            </div>
            <span className={classes('login-link')}>
                Don't have an account?{' '}
                <a href="#" onClick={() => setSelectedType(AUTH_TYPES.REGISTER)}>
                    Register
                </a>
            </span>
        </>
    )
}
